import { memo, type RefObject } from 'react';

import { FileTreeIcon, Uploads } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import { type DropZone } from '~/components/PrivateDropzone/types';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';

type MultipleZoneOverlayProps = {
  hoveredZone: DropZone | null;
  currentBoardTitle?: string;
  currentWorkspaceName?: string;
  dropZoneChooseBoardRef: RefObject<HTMLDivElement>;
  dropZoneUploadRef: RefObject<HTMLDivElement>;
};

export const MultipleZoneOverlay = memo(
  ({
    hoveredZone,
    currentBoardTitle,
    currentWorkspaceName,
    dropZoneChooseBoardRef,
    dropZoneUploadRef,
  }: MultipleZoneOverlayProps) => {
    // If the sidebar is closed, we show the equivalent of the left-zone overlay with modified messaging/iconography
    const isSidebarOpen = useWorkspaceSidebarStore((state) => state.open);
    const LeftZoneIcon = isSidebarOpen ? FileTreeIcon : Uploads;

    return (
      <div data-testid="MULTIPLE_ZONE_OVERLAY_CONTENT" className="absolute top-18 right-2 bottom-2 left-2 flex gap-3">
        <div
          ref={dropZoneChooseBoardRef}
          data-testid="MULTIPLE_ZONE_OVERLAY_LEFT_ZONE"
          className={cn(
            'flex flex-col items-center justify-center px-4 py-8',
            'rounded-12 bg-grey-1/80 backdrop-blur-[17.5px] transition-all duration-200',
            isSidebarOpen ? 'w-[calc(var(--sidebar-width)-0.75rem)]' : 'w-full',
            hoveredZone === 'choose-board'
              ? 'outline outline-2 outline-blue-9'
              : 'outline-dashed outline-1 outline-grey-7',
          )}
        >
          <div className="flex flex-col items-center gap-2">
            <LeftZoneIcon className="size-6" />
            <div className="flex flex-col items-center gap-1 text-center">
              <h2 className="font-medium text-16 text-grey-12">
                {isSidebarOpen ? 'Upload to a different board' : 'Drop files here to upload'}
              </h2>
              <p className="text-12 text-grey-10">
                Choose where to upload your files in {currentWorkspaceName || 'this workspace'}
              </p>
            </div>
          </div>
        </div>
        {isSidebarOpen && (
          <div
            ref={dropZoneUploadRef}
            data-testid="MULTIPLE_ZONE_OVERLAY_RIGHT_ZONE"
            className={cn(
              'flex flex-1 flex-col items-center justify-center',
              'rounded-12 bg-grey-1/80 backdrop-blur-[17.5px] transition-all duration-200',
              hoveredZone === 'upload' ? 'outline outline-2 outline-blue-9' : 'outline-dashed outline-1 outline-grey-7',
            )}
          >
            <div className="flex flex-col items-center gap-2">
              <Uploads className="size-6" />
              <div className="flex flex-col items-center gap-1 text-center">
                <h2 className="font-medium text-16 text-grey-12">
                  Drop files here to upload to {currentBoardTitle || 'this board'}
                </h2>
                <p className="text-12 text-grey-10">Automatically upload your assets and files to this board</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

MultipleZoneOverlay.displayName = 'MultipleZoneOverlay';
